import type { GetStaticPaths, GetStaticProps } from 'next';

import MenuPage from '@/components/menuV2/MenuPage/MenuPage';
import { MENU_CATEGORY_SELECTOR } from '@/graphql/selectors/cms';
import { Meta } from '@/layouts/Meta';
import type {
  MenuHierarchyRecordModelType,
  MenuTileRecordModelType,
} from '@/models/api';
import { initializeStore } from '@/models/root-store';
import type { MenuPageProps } from '@/shared/types';
import { Main } from '@/templates/Main';
import type { MenuBuilderDataV2 } from '@/utils/api-helpers';
import { getMenuBuilderDataV2 } from '@/utils/api-helpers';
import {
  filterActiveMenuHierarchies,
  generateShownFilters,
  generateShownSortOptions,
} from '@/utils/helpers';

const Range = ({
  menuHierarchyCms,
  filterCmsData,
  sortCmsData,
  categoryLinks,
  categoryMenuHierarchy,
  activeTiles,
  menuPath,
}: MenuPageProps) => {
  return (
    <Main
      meta={
        <Meta
          title={menuHierarchyCms?.seoTitle || 'Menu'}
          description={menuHierarchyCms?.seoDescription || 'Menu'}
          canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/menu/${menuPath.category}/${menuPath.subcategory}/${menuHierarchyCms?.slug}`}
        />
      }
    >
      <MenuPage
        menuHierarchyCms={menuHierarchyCms}
        filterCmsData={filterCmsData}
        sortCmsData={sortCmsData}
        categoryLinks={categoryLinks}
        categoryMenuHierarchy={categoryMenuHierarchy}
        activeTiles={activeTiles}
        menuPath={menuPath}
      />
    </Main>
  );
};

export const getStaticProps: GetStaticProps = async (context: any) => {
  const store = initializeStore({ ssr: true });

  const { category, subcategory, range } = context.params!;

  const {
    menuHierarchyCms,
  }: { menuHierarchyCms: MenuHierarchyRecordModelType } =
    await store.api.queryMenuHierarchyCms(
      {
        filter: {
          slug: {
            eq: range,
          },
        },
      },
      MENU_CATEGORY_SELECTOR,
    );

  const {
    filterCmsData,
    sortMenuCms,
    allMenuHierarchiesCms,
    landingPageV2Cms,
  }: MenuBuilderDataV2 = await getMenuBuilderDataV2(store);

  const filteredMenuHierarchiesCms = filterActiveMenuHierarchies(
    allMenuHierarchiesCms,
  );

  const categoryLinks = filteredMenuHierarchiesCms.map((hierarchy) => ({
    name: hierarchy.name,
    slug: hierarchy.slug,
    image: hierarchy.image,
  }));

  const categoryMenuHierarchy = filteredMenuHierarchiesCms.find(
    (hierarchy) => hierarchy.slug === category,
  );

  const tiles = landingPageV2Cms?.sections?.[0]?.tile || [];
  const activeTiles = tiles.filter(
    (tile: MenuTileRecordModelType) => tile.active === true,
  );

  const menuPath = {
    category,
    subcategory,
    range,
  };

  const shownFilters = generateShownFilters(filterCmsData, 'menu');
  const shownSortOptions = generateShownSortOptions(sortMenuCms, 'menu');
  return {
    props: {
      menuHierarchyCms,
      filterCmsData: shownFilters,
      sortCmsData: shownSortOptions,
      categoryLinks,
      categoryMenuHierarchy,
      activeTiles,
      menuPath,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const store = initializeStore({ ssr: true });

  const {
    allMenuHierarchiesCms,
  }: { allMenuHierarchiesCms: MenuHierarchyRecordModelType[] } =
    await store.api.queryAllMenuHierarchiesCms(
      {
        filter: {
          category: {
            eq: 'category',
          },
          active: {
            eq: true,
          },
        },
      },
      MENU_CATEGORY_SELECTOR,
    );

  const rangePaths = (menuHierarchies: MenuHierarchyRecordModelType[]) => {
    const paths: any = [];
    menuHierarchies.forEach((category) => {
      if (category.childrenCategories.length > 0) {
        category.childrenCategories.forEach((subcategory) => {
          if (subcategory.childrenCategories.length > 0) {
            subcategory.childrenCategories.forEach((range) => {
              paths.push({
                params: {
                  category: category.slug,
                  subcategory: subcategory.slug,
                  range: range.slug,
                },
              });
            });
          }
        });
      }
    });

    return paths;
  };

  const filteredMenuHierarchiesCms = filterActiveMenuHierarchies(
    allMenuHierarchiesCms,
  );

  const paths = rangePaths(filteredMenuHierarchiesCms);

  return {
    paths,
    fallback: false,
  };
};

export default Range;
